/**
 * Configuration and persistence for the view version switcher.
 *
 * To register a new view, add an entry to VIEW_VERSION_MAP.
 * The key is the v3 (new) route name, the value is the legacy (old) route name.
 *
 * Preferences are stored as booleans (prefers legacy: true/false) to decouple
 * stored values from route naming. This means renaming a legacy route in
 * VIEW_VERSION_MAP won't silently break existing user preferences.
 */

const STORAGE_KEY = 'view-version-preferences'

const VIEW_VERSION_MAP: Record<string, string> = {
	invoicing: 'invoicing-old'
}

const REVERSE_MAP: Record<string, string> = Object.fromEntries(
	Object.entries(VIEW_VERSION_MAP).map(([k, v]) => [v, k])
)

function getPreferences(): Record<string, boolean> {
	try {
		return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
	} catch {
		return {}
	}
}

function savePreference(newRouteName: string, prefersLegacy: boolean): void {
	const prefs = getPreferences()
	prefs[newRouteName] = prefersLegacy
	localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

/**
 * Returns the legacy route name if the user prefers the old view for a given v3 route.
 * Returns null if no preference or if the user prefers the new view.
 */
export function getPreferredLegacyRedirect(routeName: string): string | null {
	if (!(routeName in VIEW_VERSION_MAP)) return null
	const prefs = getPreferences()
	if (prefs[routeName] === true) return VIEW_VERSION_MAP[routeName]
	return null
}

export function isNewViewRoute(routeName: string): boolean {
	return routeName in VIEW_VERSION_MAP
}

export function isLegacyViewRoute(routeName: string): boolean {
	return routeName in REVERSE_MAP
}

export function getTargetRoute(routeName: string): string {
	if (routeName in VIEW_VERSION_MAP) return VIEW_VERSION_MAP[routeName]
	if (routeName in REVERSE_MAP) return REVERSE_MAP[routeName]
	return ''
}

/**
 * Gets the canonical (new) route name for a given route.
 * Works for both new and legacy route names.
 */
function getNewRouteName(routeName: string): string {
	if (routeName in VIEW_VERSION_MAP) return routeName
	if (routeName in REVERSE_MAP) return REVERSE_MAP[routeName]
	return ''
}

/**
 * Persist that the user prefers the target route.
 * Call this when the user clicks the switch button.
 */
export function persistViewPreference(currentRouteName: string, targetRouteName: string): void {
	const newRoute = getNewRouteName(currentRouteName)
	if (newRoute) {
		const prefersLegacy = isLegacyViewRoute(targetRouteName)
		savePreference(newRoute, prefersLegacy)
	}
}
